import type { ReactNode, Ref } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { match } from 'ts-pattern';

import { type ClientListItem, clientByIdOptions, type SelectedClient } from '@/entities/client';
import { useSheetStore } from '@/shared/stores/sheet-store';
import { SearchCombobox } from '@/shared/ui/search-combobox/search-combobox';

import { useClientSearch } from '../../model/use-client-search';
import { SearchClientHint } from './search-client-field/search-client-hint';

type Props = {
    variant: 'inline' | 'navigation';
    layout: 'dropdown' | 'panel';
    hasEhrAccess: boolean;
    onSelectClient: (client: ClientListItem) => void;
    selectedClient?: SelectedClient | null;
    hasError?: boolean;
    onClear?: () => void;
    onClose?: () => void;
    onAddNewClient?: () => void;
    startSessionOnCreate?: boolean;
    leading?: ReactNode;
    ref?: Ref<HTMLInputElement>;
};

export function SearchClientField({
    variant,
    layout,
    hasEhrAccess,
    selectedClient = null,
    hasError = false,
    startSessionOnCreate = false,
    onSelectClient,
    onClear,
    onClose,
    onAddNewClient,
    leading,
    ref,
}: Props) {
    const openSheet = useSheetStore((state) => state.openSheet);
    const queryClient = useQueryClient();

    const { searchTerm, setSearchTerm, clients, hasData, isError } = useClientSearch();

    const handleSelectClient = (client: ClientListItem) => {
        queryClient.setQueryData(clientByIdOptions(client.id).queryKey, client);
        setSearchTerm('');
        onSelectClient(client);
    };

    const handleAddNewClient = () => {
        // Add Client ends on the chart, so a flow that goes on to start a session keeps quick-add.
        if (hasEhrAccess && !startSessionOnCreate) {
            openSheet({
                type: 'clients/add',
                label: 'New Client',
                data: { prefillName: searchTerm },
            });
        } else {
            openSheet({
                type: 'clients/quick-add',
                label: 'Add Client',
                data: { prefillName: searchTerm, startSessionOnCreate },
            });
        }
        setSearchTerm('');
        onAddNewClient?.();
    };

    const handleSearchTermChange = (value: string) => {
        onClear?.();
        setSearchTerm(value);
    };

    const handleClear = () => {
        onClear?.();
        setSearchTerm('');
    };

    const placeholder = match({ variant, layout })
        .with({ variant: 'inline' }, () => "Start typing client's name")
        .with({ layout: 'panel' }, () => 'Search clients')
        .otherwise(() => 'Search...');

    return (
        <SearchCombobox
            ref={ref}
            variant={variant}
            layout={layout}
            items={clients}
            getKey={(client) => client.id}
            getLabel={(client) => client.name}
            searchTerm={searchTerm}
            onSearchTermChange={handleSearchTermChange}
            hasData={hasData}
            isError={isError}
            selectedLabel={selectedClient?.name}
            onSelect={handleSelectClient}
            onClear={handleClear}
            onClose={onClose}
            emptyAction={{ label: 'Add new client', onAction: handleAddNewClient }}
            hint={
                variant === 'inline'
                    ? (hintId) => (
                          <SearchClientHint
                              id={hintId}
                              hasError={hasError}
                              onAddNewClient={handleAddNewClient}
                          />
                      )
                    : undefined
            }
            label={variant === 'inline' ? 'Select a client' : undefined}
            placeholder={placeholder}
            resultsLabel="Client results"
            hasError={hasError}
            leading={leading}
        />
    );
}
